import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigation } from '../../context/NavigationContext';
import { useBasket } from '../../context/BasketContext';
import useRequest from 'hooks/useRequest';
import {
  BringzzPageContainer,
  BringzzIcon,
  BringzzText,
  BringzzImage,
  BringzzButton,
  BringzzCallToAction,
  BringzzBasketDrawer
} from "@bringzz/components";
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'context/AuthContext';
import { useBookmark } from 'context/BookmarkContext';
import BookmarkCollectionDrawer from '../../components/BookmarkCollectionDrawer';
import { Carousel } from 'react-responsive-carousel';
import ManCallToAction from 'assets/images/ManCallToAction.png';
import DefaultProductImage from 'assets/DefaultProductImage.png';
import { USER_ROLES } from 'utils/constants';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

const slideUpAnimation = `
  @keyframes slide-up {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
  .animate-slide-up {
    animation: slide-up 0.3s ease-out;
  }
`;

if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = slideUpAnimation;
  document.head.appendChild(style);
}

function ProductDetails() {
  const extractProductUuid = () => {
    const pathSegments = window.location.pathname.split('/');
    return pathSegments[pathSegments.length - 1];
  };

  const { productUuid: paramProductUuid } = useParams();
  const productUuid = paramProductUuid || extractProductUuid();
  const { sendRequest } = useRequest();
  const { backPress, navigate } = useNavigation();
  const { addToBasket, basketItems } = useBasket();

  const [productData, setProductData] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddToBasketModal, setShowAddToBasketModal] = useState(false);
  const [showPackagingModal, setShowPackagingModal] = useState(false);
  const [selectedConfiguration, setSelectedConfiguration] = useState({
    quantity: 1,
    packaging: '',
  });
  const [addedProduct, setAddedProduct] = useState(null);
  const [bookmarkStatus, setBookmarkStatus] = useState(null);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const teaserRef = useRef(null);
  const [selectedModal, setSelectedModal] = useState(null);

  // Bookmark collection states
  const [showBookmarkDrawer, setShowBookmarkDrawer] = useState(false);

  const { t } = useTranslation();
  const { user } = useAuth();
  const { collections, createCollection } = useBookmark();

  // Configurable threshold for limited stock
  // const LIMITED_STOCK_THRESHOLD = 10;

  const [expandedSections, setExpandedSections] = useState({
    description: true,
    application: false,
    preparation: false,
    administrationHelp: false,
    ingredients: false,
    storage: false,
    storage_information: false,
    symptoms: false,
    diseases: false,
    sideEffects: false,
    productGroup: false
  });

  // Create images array with main product image and gallery images
  const images = React.useMemo(() => {
    const allImages = [];

    // Add main product image first
    if (productData?.productImage) {
      allImages.push(productData.productImage);
    }

    // Add gallery images
    if (productData?.gallery && Array.isArray(productData.gallery)) {
      productData.gallery.forEach(img => {
        if (img && !allImages.includes(img)) {
          allImages.push(img);
        }
      });
    }

    // Fallback to default product image if no images
    return allImages.length > 0 ? allImages : [DefaultProductImage];
  }, [productData]);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchProductDetails();
  }, [productUuid, user]);

  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await sendRequest({
        method: 'GET',
        url: `/module/pharmacyproduct/getProductDetails?productUuid=${productUuid}`,
      });

      if (response?.data) {
        const productData = response.data;

        setProductData(productData);
        setSelectedConfiguration({
          quantity: 1,
          packaging: productData.packaging || '',
        });

        // Set bookmark status from isBookmarked flag
        setBookmarkStatus(productData.isBookmarked ? 'success' : 'error');

        // Check if teaser needs truncation after data loads
        setTimeout(() => {
          if (teaserRef.current && productData.teaser) {
            const lineHeight = 24; // approximate line height
            const maxHeight = lineHeight * 2; // 2 lines
            setNeedsTruncation(teaserRef.current.scrollHeight > maxHeight);
          }
        }, 100);
      } else {
        throw new Error(t('component.product_details.error.not_found'));
      }
    } catch (err) {
      setError(err.message || t('component.product_details.error.failed_to_load'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddToBasket = async () => {
    try {
      const result = await addToBasket({
        integrationId: productData.integrationId,
        productIntegrationId: productData.integrationId,
        quantity: selectedConfiguration.quantity,
        price: productData.price,
        imgUrl: productData.productImage || DefaultProductImage,
        manufacturer: productData.pharmacyId_Dyn_Sort || productData.pharmacyName,
        name: productData.name,
        amount: `${productData.packageQuantity} ${productData.packaging}`
      });

      if (result?.status === 'success') {
        setAddedProduct({
          ...productData,
          quantity: selectedConfiguration.quantity,
          price: productData.price,
          imgUrl: productData.productImage || DefaultProductImage,
          manufacturer: productData.pharmacyId_Dyn_Sort || productData.pharmacyName,
          name: productData.name,
          amount: `${productData.packageQuantity} ${productData.packaging}`
        });
        setShowAddToBasketModal(true);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };

  const handleContinueShopping = () => {
    setShowAddToBasketModal(false);
    // Keep the added product so it shows correctly when reopened
  };

  const handleGoToBasket = () => {
    navigate("/basket");
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t('component.product_details.share.url_copied'));
    } catch (error) {
      console.error('Failed to copy URL:', error);
      toast.error(t('component.product_details.toast.url_empty'));
    }
  };

  const checkBookmarkStatus = async (integrationId) => {
    try {
      const response = await sendRequest({
        method: 'POST',
        url: `/module/bookmark/hasBookmark`,
        data: {
          elementUUID: integrationId,
          type: 1
        }
      });
      return response.status;
    } catch (error) {
      return false;
    }
  };

  const addBookmark = async (integrationId) => {
    try {
      const response = await sendRequest({
        method: 'POST',
        url: `/module/bookmark/addBookmark`,
        data: {
          type: 1,
          elementUUID: integrationId
        }
      });
      if (response.status === 'success') {
        setBookmarkStatus('success');
      }
    } catch (error) {
      console.error('Error adding bookmark:', error);
    }
  };

  const deleteBookmark = async (integrationId) => {
    try {
      const response = await sendRequest({
        method: 'POST',
        url: `/module/bookmark/removeBookmark`,
        data: {
          elementUUID: integrationId,
          type: 1
        }
      });
      if (response.status === 'success') {
        setBookmarkStatus('error');
        toast.success(t('component.bookmarks.bookmark_removed'));
      }
    } catch (error) {
      console.error('Error removing bookmark:', error);
      toast.error(t('component.bookmarks.bookmark_error'));
    }
  };

  const addBookmarkToCollection = async (integrationId, collectionUuid = null) => {
    try {
      const response = await sendRequest({
        method: 'POST',
        url: `/module/bookmark/addBookmark`,
        data: {
          type: 1,
          elementUUID: integrationId,
          ...(collectionUuid && { collectionUUID: collectionUuid }),
        },
      });

      if (response.status === 'success') {
        setBookmarkStatus('success');
        setShowBookmarkDrawer(false);
        const bookmarkUrl = collectionUuid ? `/settings/bookmarks/collection?id=${collectionUuid}&tab=products` : '/settings/bookmarks';
        toast.success(
          <div>
            {t('component.bookmarks.bookmark_success')}{' '}
            <span
              onClick={() => navigate(bookmarkUrl)}
              className="underline cursor-pointer text-blue-600 hover:text-blue-800"
            >
              View bookmarks
            </span>
          </div>
        );
      }
    } catch (error) {
      console.error('Error adding bookmark:', error);
      toast.error(t('component.bookmarks.bookmark_error'));
    }
  };

  const createNewCollection = async (name) => {
    const success = await createCollection(name);
    if (success) {
      toast.success(t('component.bookmarks.collection_created'));
    } else {
      toast.error(t('component.bookmarks.bookmark_error'));
    }
  };

  const handleBookmark = async () => {
    if (!productData?.integrationId) return;

    if (!user) {
      navigate('/login');
      return;
    }

    if (bookmarkStatus === 'success') {
      await deleteBookmark(productData.integrationId);
    } else {
      setShowBookmarkDrawer(true);
    }
  };

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity >= 1) {
      setSelectedConfiguration({
        ...selectedConfiguration,
        quantity: newQuantity
      });
    }
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const formatPrice = (price) => `€${price.toFixed(2)}`;

  // Helper function to determine product availability status
  const getAvailabilityStatus = () => {
    const availability = productData?.availability;
    const maxQuantity = productData?.maxQuantity || 0;

    // PRIORITY 1: Check availability field from product table
    // If availability is 0 or null -> UNAVAILABLE (regardless of stock)
    if (availability === 0 || availability === null) {
      return 'unavailable';
    }

    // PRIORITY 2: availability === 1, now check stock quantity
    if (availability === 1) {
      // No stock in pharmacies -> UNAVAILABLE
      if (maxQuantity === 0) {
        return 'unavailable';
      }
      // Limited stock: quantity > 0 AND < threshold
      // if (maxQuantity > 0 && maxQuantity < LIMITED_STOCK_THRESHOLD) {
      //   return 'limited';
      // }
      // Available: quantity >= threshold
      // if (maxQuantity >= LIMITED_STOCK_THRESHOLD) {
      //   return 'available';
      // }
      // Available: any quantity > 0
      if (maxQuantity > 0) {
        return 'available';
      }
    }

    // Default fallback
    return 'unavailable';
  };

  const getAssetsByType = (type) => {
    if (!productData?.assets) return [];
    return productData.assets.filter(asset => asset.assetType === type).map(asset => asset.assetUrl).filter(url => url);
  };

  const decodePictoCodes = () => {
    const pictoMap = {
      "1": "Einnahme nüchtern",
      "2": "Einnahme vor der Mahlzeit",
      "3": "Einnahme zu der Mahlzeit",
      "4": "Einnahme nach der Mahlzeit",
      "6": "Vorsicht Diabetiker",
      "7": "Einnahme unversehrt",
      "8": "Vorsicht Einfluss auf Verkehrstüchtigkeit/Bedienen von Maschinen",
      "9": "Alkoholeinnahme vermeiden",
      "D": "Bei KadersportlerInnen gemäß der Verbotsliste der WADA in der jeweils gültigen Fassung absolut verboten, TUE (Therapeutic Use Exemptions) aber möglich.",
      "d": "Bedingt bei KadersportlerInnen verboten, hier sind spezielle Regeln zu beachten.",
      "A": "Schwarzes Dreieck. Das Arzneimittel unterliegt einer\nzusätzlichen Überwachung."
    };

    const sstMap = {
      "1": "Anwendung erlaubt",
      "2": "Anwendung verboten",
      "3": "Anwendung nur bei zwingender oder vitaler Indikation",
      "4": "Anwendung unter sorgfältiger Kontrolle möglich",
      "-": "Es kann keine genaue Aussage gemacht werden.",
      "k": "Kinderform"
    };

    const sstzMap = {
      "1": "Stillen erlaubt",
      "2": "Stillen verboten",
      "3": "Stillen nur bei zwingender oder vitaler Indikation",
      "4": "Stillen unter sorgfältiger Kontrolle möglich",
      "-": "Es kann keine genaue Aussage gemacht werden.",
      "k": "Kinderform"
    };
    const results = [];

    if (productData?.picto_code_flags) {
      const pictoResults = productData.picto_code_flags.split('').map(code => pictoMap[code]).filter(Boolean);
      results.push(...pictoResults);
    }

    if (productData?.pregnancy_information) {
      const sstResult = sstMap[productData.pregnancy_information];
      if (sstResult) results.push(`Schwangerschaft: ${sstResult}`);
    }

    if (productData?.breastfeeding_instructions) {
      const sstzResult = sstzMap[productData.breastfeeding_instructions];
      if (sstzResult) results.push(`Stillzeit: ${sstzResult}`);
    }

    return results;
  };

  const decodeStorageInfo = (storageCode) => {
    if (!storageCode) return null;
    const storageMap = {
      "E": "Kühlschrank (Refrigerator)",
      "K": "Kühlschrank (Refrigerator)",
      "T": "Tiefgekühlt (Frozen)"
    };
    return storageMap[storageCode] || storageCode;
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-purple-600 rounded-full animate-spin mb-4"></div>
        <BringzzText className="text-gray-600">{t('component.product_details.loading')}</BringzzText>
      </div>
    );
  }

  if (error) {
    return (
      <BringzzPageContainer>
        <div className="min-h-screen flex flex-col">
          <div className="relative w-full h-10 z-20 flex justify-between items-center px-4 pt-4">
            <BringzzIcon
              size="24"
              icon="IconChevronLeft"
              folder="LineIcons"
              className="cursor-pointer text-gray-700 hover:text-gray-900 transition-colors"
              onClick={backPress}
            />
          </div>

          <div className="flex-1 flex flex-col items-center justify-center px-6 pb-20">
            <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mb-4">
              <BringzzIcon icon="IconClose" folder="LineIcons" size="32" className="text-white" />
            </div>

            <BringzzText tag="h2" className="text-xl font-bold text-gray-900 mb-2 text-center">
              {t('component.product_details.error.product_not_found')}
            </BringzzText>
            <BringzzText className="text-sm text-gray-600 text-center mb-6 max-w-xs">
              {t('component.product_details.error.product_not_found_description')}
            </BringzzText>

            <div className="flex flex-col gap-2 w-full max-w-xs">
              <BringzzButton
                onClick={fetchProductDetails}
                className="w-full bg-purple-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-purple-700 transition-colors text-sm"
              >
                {t('component.product_details.error.try_again')}
              </BringzzButton>
              <BringzzButton
                onClick={() => navigate('/')}
                className="w-full bg-gray-100 text-gray-700 px-5 py-2.5 rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm"
              >
                {t('component.product_details.error.go_home')}
              </BringzzButton>
            </div>
          </div>
        </div>
      </BringzzPageContainer>
    );
  }

  if (!productData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <BringzzText className="text-gray-600">{t('component.product_details.error.not_found')}</BringzzText>
      </div>
    );
  }
  const discount = productData.avpInEuro > productData.price ?
    Math.round(((productData.avpInEuro - productData.price) / productData.avpInEuro) * 100) : 0;

  const footerContent = user && user.userRole !== USER_ROLES.PHARMACY_ROLE ? (
    <div className='p-4 shadow-top bg-white'>
      <div className="flex items-center justify-between w-full">
        <div className="flex flex-col">
          {(() => {
            const status = getAvailabilityStatus();
            if (status === 'available') {
              return (
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <BringzzText className="text-xs text-purple-600 font-medium">{t('component.product_details.availability.available')}</BringzzText>
                </div>
              );
            } /* else if (status === 'limited') {
              return (
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                  <BringzzText className="text-xs text-orange-600 font-medium">{t('component.product_details.availability.limited_stock')}</BringzzText>
                </div>
              );
            } */ else {
              return (
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <BringzzText className="text-xs text-red-600 font-medium">{t('component.product_details.availability.unavailable')}</BringzzText>
                </div>
              );
            }
          })()}
          <div className="flex items-baseline gap-2">
            {productData.price && (
              <BringzzText className="text-xl font-bold text-gray-900">
                {formatPrice(productData.price)}
              </BringzzText>
            )}
            {productData.kpInEuro != null && productData.kpInEuro !== 0 && (
              <BringzzText className="text-xs text-gray-500">
                {t('component.product_details.kassenpreis')} {formatPrice(productData.kpInEuro)}
              </BringzzText>
            )}
          </div>
        </div>

        {getAvailabilityStatus() === 'unavailable' ? (
          <button
            onClick={() => navigate('/search')}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 font-semibold text-sm transition-all duration-200 rounded-full flex items-center gap-2"
          >
            {t('component.product_details.buttons.search_alternative')}
          </button>
        ) : (productData.prescription_ind_symbol === "+" || productData.prescription_ind_symbol === "++") ? (
          <button
            onClick={() => navigate('/scan')}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 font-semibold text-sm transition-all duration-200 rounded-full flex items-center gap-2"
          >
            <BringzzIcon icon="IconScan" folder="LineIcons" size="18" className="text-white" />
            {t('component.product_details.buttons.scan_prescription')}
          </button>
        ) : (
          <div className="flex items-center h-10">
            <div className="flex items-center bg-gray-100 border border-gray-300 rounded-l-full h-10">
              <button
                onClick={() => handleQuantityChange(selectedConfiguration.quantity - 1)}
                className="w-10 h-10 flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-l-full"
                disabled={selectedConfiguration.quantity <= 1}
              >
                <BringzzIcon icon="IconMinus" folder="LineIcons" size="16" className="text-current" />
              </button>

              <span className="text-base font-semibold text-gray-900 min-w-[3rem] text-center px-3 h-10 flex items-center">
                {selectedConfiguration.quantity}
              </span>

              <button
                onClick={() => handleQuantityChange(selectedConfiguration.quantity + 1)}
                className="w-10 h-10 flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-all duration-200"
              >
                <BringzzIcon icon="IconPlus" folder="LineIcons" size="16" className="text-current" />
              </button>
            </div>

            <button
              onClick={handleAddToBasket}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 font-semibold text-sm transition-all duration-200 rounded-r-full border border-l-0 border-purple-600 h-10 flex items-center"
            >
              {t('component.product_details.buttons.add')}
            </button>
          </div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <BringzzPageContainer footer={footerContent} footerHover={true} className="product-details-page">
      <div className="overflow-x-hidden">
        <div className="flex flex-col min-h-screen justify-center md:max-w-2xl w-full">
        <div className="relative" style={{ minHeight: productData.teaser ? '600px' : '480px', height: showFullDescription ? 'auto' : (productData.teaser ? '600px' : '480px') }}>
          <div className="relative w-full h-10 z-20 flex justify-between items-center px-4 pt-4 text-white">
            <div className="flex space-x-1">
              <BringzzIcon
                size="24"
                icon="IconChevronLeft"
                folder="LineIcons"
                className="cursor-pointer text-gray-700 hover:text-gray-900 transition-colors"
                onClick={backPress}
              />
            </div>
            <div className="flex space-x-3"></div>
          </div>

          <div className="z-10 absolute inset-0 w-full h-full bg-gradient-to-b from-black/40 to-transparent"></div>
          <div className="absolute inset-0 w-full" style={{ height: showFullDescription ? '100%' : '100%', minHeight: '100%' }}>
            <BringzzImage
              className="object-cover w-full blur-2xl scale-110 opacity-25"
              style={{ height: showFullDescription ? '100%' : '100%', minHeight: '100%' }}
              src={images[0]}
              alt="Background"
            />
          </div>

          <div className="relative w-full" style={{ minHeight: '100%' }}>
            <div className="relative w-full overflow-hidden">

              <div className="relative z-10 w-full h-80 sm:h-80">
                {images.length > 1 ? (
                  <Carousel
                    showArrows={true}
                    showStatus={false}
                    showThumbs={false}
                    infiniteLoop={true}
                    autoPlay={true}
                    interval={4000}
                    stopOnHover={true}
                    swipeable={true}
                    emulateTouch={true}
                    className="h-full"
                    renderArrowPrev={(onClickHandler, hasPrev, label) => (
                      hasPrev && (
                        <button
                          type="button"
                          onClick={onClickHandler}
                          title={label}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 z-20 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all duration-200"
                        >
                          <BringzzIcon
                            icon="IconChevronLeft"
                            folder="LineIcons"
                            size="18"
                            className="text-gray-700"
                          />
                        </button>
                      )
                    )}
                    renderArrowNext={(onClickHandler, hasNext, label) => (
                      hasNext && (
                        <button
                          type="button"
                          onClick={onClickHandler}
                          title={label}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 z-20 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all duration-200"
                        >
                          <BringzzIcon
                            icon="IconChevronRight"
                            folder="LineIcons"
                            size="18"
                            className="text-gray-700"
                          />
                        </button>
                      )
                    )}
                  >
                    {images.map((image, index) => (
                      <div key={index} className="relative w-full h-80 sm:h-80">
                        <BringzzImage
                          className="object-contain w-full h-full"
                          src={image}
                          alt={`${productData?.name || 'Product'} - Image ${index + 1}`}
                        />
                      </div>
                    ))}
                  </Carousel>
                ) : (
                  <div className="relative w-full h-80 sm:h-80">
                    <BringzzImage
                      className="object-contain w-full h-full"
                      src={images[0]}
                      alt={productData?.name || 'Product image'}
                    />
                  </div>
                )}
                {images.length > 1 && (
                  <div className="absolute top-4 left-14 z-20">
                    <div className="bg-black/60 text-white px-2 py-1 rounded-full text-xs font-medium">
                      {t('component.product_details.images.photos_count', { count: images.length })}
                    </div>
                  </div>
                )}
              </div>
              <div className="relative z-20 px-4 py-6">
                <div className="flex items-start gap-3">
                  <BringzzText tag="h1" className="text-2xl font-bold text-gray-900 flex-1">
                    {productData.name}
                  </BringzzText>
                  {(productData.prescription_ind_symbol === "+" || productData.prescription_ind_symbol === "++") && (
                    <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded-full border border-blue-200">
                      Rx
                    </span>
                  )}
                </div>

                {productData.teaser && (
                  <div className="mt-4">
                    <div className="relative">
                      <div
                        ref={teaserRef}
                        className={`text-gray-700 leading-relaxed prose prose-sm max-w-none prose-h2:font-bold prose-h3:font-bold prose-h4:font-bold prose-strong:font-bold ${!showFullDescription && needsTruncation ? 'line-clamp-2 pr-20' : ''}`}
                        dangerouslySetInnerHTML={{ __html: productData.teaser }}
                      />
                      {!showFullDescription && needsTruncation && (
                        <button
                          onClick={() => setShowFullDescription(true)}
                          className="absolute bottom-0 right-0 text-purple-600 text-sm font-medium hover:text-purple-700 transition-colors pl-2"
                        >
                          {t('component.product_details.buttons.read_more')}
                        </button>
                      )}
                    </div>
                    {showFullDescription && needsTruncation && (
                      <button
                        onClick={() => setShowFullDescription(false)}
                        className="text-purple-600 text-sm font-medium mt-2 hover:text-purple-700 transition-colors"
                      >
                        {t('component.product_details.buttons.read_less')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

          <div className="pb-6">
            <div className="mt-8 px-4">
              <div className="flex items-center gap-4 mb-8">
                <div className="flex flex-col items-start">
                  <BringzzText className="text-xs font-bold text-gray-900 mb-2">
                    {productData.distributorName || t('component.product_details.unknown_distributor')}
                  </BringzzText>
                  <BringzzText className="text-sm font-medium text-gray-700">{t('component.product_details.manufacturer')}</BringzzText>
                </div>

                <div className="w-0.5 self-stretch bg-purple-500 mx-1"></div>

                <div className="flex gap-3 flex-1">
                  {(productData.formLabel || productData.pharmaForm) && (
                    <button
                      onClick={() => setSelectedModal('form')}
                      className="flex-1 bg-gray-100 border border-black rounded-lg py-4 px-3 flex flex-col items-center justify-center hover:bg-gray-200 transition-colors"
                    >
                      <BringzzIcon icon="IconPill1" folder="HealthIcons" size="24" className="text-gray-900 mb-2" />
                      <span className="text-sm font-bold text-gray-900">{t('component.product_details.buttons.form')}</span>
                    </button>
                  )}

                  {(productData.strengthEntries?.length > 0 || productData.preparation_information) && (
                    <button
                      onClick={() => setSelectedModal('strength')}
                      className="flex-1 bg-gray-100 border border-black rounded-lg py-4 px-3 flex flex-col items-center justify-center hover:bg-gray-200 transition-colors"
                    >
                      <BringzzIcon icon="IconWeight" folder="HealthIcons" size="24" className="text-gray-900 mb-2" />
                      <span className="text-sm font-bold text-gray-900">{t('component.product_details.buttons.strength')}</span>
                    </button>
                  )}

                  {(productData.packageQuantity || productData.packaging) && (
                    <button
                      onClick={() => setSelectedModal('package')}
                      className="flex-1 bg-gray-100 border border-black rounded-lg py-4 px-3 flex flex-col items-center justify-center hover:bg-gray-200 transition-colors"
                    >
                      <BringzzIcon icon="IconPrescriptionDocument" folder="HealthIcons" size="24" className="text-gray-900 mb-2" />
                      <span className="text-sm font-bold text-gray-900">{t('component.product_details.buttons.package')}</span>
                    </button>
                  )}
                </div>
              </div>

              <div className="flex justify-end items-center gap-3 mb-8 px-4">
                <BringzzIcon
                  size="24"
                  icon={bookmarkStatus === 'success' ? "IconBookmarkSolid" : "IconBookmark"}
                  folder={bookmarkStatus === 'success' ? "HeroIcons" : "LineIcons"}
                  className="cursor-pointer text-gray-700 hover:text-blue-500 transition-colors"
                  onClick={handleBookmark}
                />
                <BringzzIcon
                  size="24"
                  icon="IconShare"
                  folder="LineIcons"
                  className="cursor-pointer text-gray-700 hover:text-gray-900 transition-colors"
                  onClick={handleShare}
                />
              </div>
            </div>

            <div className="space-y-2 mb-8 px-4">
              {productData.description && (
                <div className="bg-white rounded-lg border border-gray-200">
                  <button
                    onClick={() => toggleSection('description')}
                    className="w-full px-4 py-4 flex items-center justify-between text-left"
                  >
                    <div className="flex items-center gap-3">
                      <BringzzIcon icon="IconClinicalF" folder="HealthIcons" size="20" className="text-gray-600" />
                      <BringzzText className="font-semibold text-gray-900">{t('component.product_details.sections.description')}</BringzzText>
                    </div>
                    <BringzzIcon
                      icon={expandedSections.description ? "IconChevronUp" : "IconChevronDown"}
                      folder="LineIcons"
                      size="20"
                      className="text-gray-600 transition-transform duration-200"
                    />
                  </button>
                  {expandedSections.description && (
                    <div className="px-4 pb-4">
                      <div className="h-1 bg-purple-500 rounded-full mb-4"></div>
                      <BringzzText className="text-gray-700 leading-relaxed">
                        {productData.description.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ')}
                      </BringzzText>
                    </div>
                  )}
                </div>
              )}
              {(productData.breastfeeding_instructions || productData.pregnancy_information || productData.picto_code_flags) && (
                <div className="bg-white rounded-lg border border-gray-200">
                  <button
                    onClick={() => toggleSection('application')}
                    className="w-full px-4 py-4 flex items-center justify-between text-left"
                  >
                    <div className="flex items-center gap-3">
                      <BringzzIcon icon="IconUser" folder="LineIcons" size="20" className="text-gray-600" />
                      <BringzzText className="font-semibold text-gray-900">{t('component.product_details.sections.application')}</BringzzText>
                    </div>
                    <BringzzIcon
                      icon={expandedSections.application ? "IconChevronUp" : "IconChevronDown"}
                      folder="LineIcons"
                      size="20"
                      className="text-gray-600 transition-transform duration-200"
                    />
                  </button>
                  {expandedSections.application && (
                    <div className="px-4 pb-4">
                      {decodePictoCodes().map((instruction, index) => (
                        <BringzzText key={index} className="text-gray-700 leading-relaxed mb-2">
                          {instruction}
                        </BringzzText>
                      ))}
                    </div>
                  )}
                </div>
              )}

            {productData.preparation_information && (
              <div className="bg-white rounded-lg border border-gray-200">
                <button
                  onClick={() => toggleSection('preparation')}
                  className="w-full px-4 py-4 flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3">
                    <BringzzIcon icon="IconFlask" folder="LineIcons" size="20" className="text-gray-600" />
                    <BringzzText className="font-semibold text-gray-900">{t('component.product_details.sections.application')}</BringzzText>
                  </div>
                  <BringzzIcon 
                    icon={expandedSections.preparation ? "IconChevronUp" : "IconChevronDown"} 
                    folder="LineIcons" 
                    size="20" 
                    className="text-gray-600 transition-transform duration-200" 
                  />
                </button>
                {expandedSections.preparation && (
                  <div className="px-4 pb-4">
                    <BringzzText className="text-gray-700 leading-relaxed">
                      {productData.preparation_information.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ')}
                    </BringzzText>
                  </div>
                )}
              </div>
            )}
            {productData.administrationHelp && (
              <div className="bg-white rounded-lg border border-gray-200">
                <button
                  onClick={() => toggleSection('administrationHelp')}
                  className="w-full px-4 py-4 flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3">
                    <BringzzIcon icon="IconFlask" folder="LineIcons" size="20" className="text-gray-600" />
                    <BringzzText className="font-semibold text-gray-900">{t('component.product_details.sections.administration')}</BringzzText>
                  </div>
                  <BringzzIcon 
                    icon={expandedSections.administrationHelp ? "IconChevronUp" : "IconChevronDown"} 
                    folder="LineIcons" 
                    size="20" 
                    className="text-gray-600 transition-transform duration-200" 
                  />
                </button>
                {expandedSections.administrationHelp && (
                  <div className="px-4 pb-4">
                    <div className="h-1 bg-purple-500 rounded-full mb-4"></div>
                    <BringzzText className="text-gray-700 leading-relaxed">
                      {productData.administrationHelp.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ')}
                    </BringzzText>
                  </div>
                )}
              </div>
            )}

            {productData.storage_information && (
              <div className="bg-white rounded-lg border border-gray-200">
                <button
                  onClick={() => toggleSection('storage_information')}
                  className="w-full px-4 py-4 flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3">
                    <BringzzIcon icon="IconFlask" folder="LineIcons" size="20" className="text-gray-600" />
                    <BringzzText className="font-semibold text-gray-900">{t('component.product_details.sections.storage_information')}</BringzzText>
                  </div>
                  <BringzzIcon 
                    icon={expandedSections.storage_information ? "IconChevronUp" : "IconChevronDown"} 
                    folder="LineIcons" 
                    size="20" 
                    className="text-gray-600 transition-transform duration-200" 
                  />
                </button>
                {expandedSections.storage_information && (
                  <div className="px-4 pb-4">
                    <div className="h-1 bg-purple-500 rounded-full mb-4"></div>
                    <BringzzText className="text-gray-700 leading-relaxed">
                      {decodeStorageInfo(productData.storage_information)}
                    </BringzzText>
                  </div>
                )}
              </div>
            )}

            {productData.sideEffectsHelp && (
              <div className="bg-white rounded-lg border border-gray-200">
                <button
                  onClick={() => toggleSection('sideEffects')}
                  className="w-full px-4 py-4 flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3">
                    <BringzzIcon icon="IconHeartCardiogram" folder="HealthIcons" size="20" className="text-gray-600" />
                    <BringzzText className="font-semibold text-gray-900">{t('component.product_details.sections.side_effects')}</BringzzText>
                  </div>
                  <BringzzIcon 
                    icon={expandedSections.sideEffects ? "IconChevronUp" : "IconChevronDown"} 
                    folder="LineIcons" 
                    size="20" 
                    className="text-gray-600 transition-transform duration-200" 
                  />
                </button>
                {expandedSections.sideEffects && (
                  <div className="px-4 pb-4">
                    <div className="h-1 bg-purple-500 rounded-full mb-4"></div>
                    <BringzzText className="text-gray-700 leading-relaxed">
                      {productData.sideEffectsHelp.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ')}
                    </BringzzText>
                  </div>
                )}
              </div>
            )}
            {/* 
            {(productData.groupKey || productData.groupDescription) && (
              <div className="bg-white rounded-lg border border-gray-200">
                <button
                  onClick={() => toggleSection('productGroup')}
                  className="w-full px-4 py-4 flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3">
                    <BringzzIcon icon="IconTag" folder="LineIcons" size="20" className="text-gray-600" />
                    <BringzzText className="font-semibold text-gray-900">{t('component.product_details.sections.product_group')}</BringzzText>
                  </div>
                  <BringzzIcon 
                    icon={expandedSections.productGroup ? "IconChevronUp" : "IconChevronDown"} 
                    folder="LineIcons" 
                    size="20" 
                    className="text-gray-600 transition-transform duration-200" 
                  />
                </button>
                {expandedSections.productGroup && (
                  <div className="px-4 pb-4">
                    {productData.groupKey && (
                      <div className="mb-2">
                        <BringzzText className="font-medium text-gray-900 mb-1">{t('component.product_details.product_group.group')}:</BringzzText>
                        <BringzzText className="text-gray-700">{productData.groupKey}</BringzzText>
                      </div>
                    )}
                    {productData.groupDescription && (
                      <div>
                        <BringzzText className="font-medium text-gray-900 mb-1">{t('component.product_details.product_group.description')}:</BringzzText>
                        <BringzzText className="text-gray-700 leading-relaxed">{productData.groupDescription}</BringzzText>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            */}

            </div>

            <div className="flex flex-wrap gap-3 mb-8 px-4">
              <div className="bg-white rounded-lg border border-gray-200 p-3 text-center flex-1 min-w-0">
                <div className="flex justify-center mb-2">
                  <BringzzIcon icon="IconRx" folder="HealthIcons" size="24" className="text-gray-600" />
                </div>
                <BringzzText className="text-xs font-bold text-gray-900 mb-1">{t('component.product_details.info_cards.prescription.title')}</BringzzText>
                <BringzzText className="text-xs font-bold text-gray-900">{(productData.prescription_ind_symbol === "+" || productData.prescription_ind_symbol === "++") ? t('component.product_details.info_cards.prescription.needed') : t('component.product_details.info_cards.prescription.not_needed')}</BringzzText>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-3 text-center flex-1 min-w-0">
                <div className="flex justify-center mb-2">
                  <BringzzIcon icon="IconBarCodeSearch" folder="LineIcons" size="24" className="text-gray-600" />
                </div>
                <BringzzText className="text-xs font-bold text-gray-900 mb-1">PZN</BringzzText>
                <BringzzText className="text-xs font-bold text-gray-900">{productData.pzn}</BringzzText>
              </div>

              {productData.patientLeafletUrl && (
                <div
                  className="bg-white rounded-lg border border-gray-200 p-3 text-center flex-1 min-w-0 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => window.open(productData.patientLeafletUrl, '_blank')}
                >
                  <div className="flex justify-center mb-2">
                    <BringzzIcon icon="IconFile" folder="LineIcons" size="24" className="text-gray-600" />
                  </div>
                  <BringzzText className="text-xs font-bold text-gray-900 mb-1">{t('component.product_details.info_cards.patient_leaflet.title')}</BringzzText>
                  <BringzzText className="text-xs font-bold text-gray-900">{t('component.product_details.info_cards.patient_leaflet.subtitle')}</BringzzText>
                </div>
              )}
              {/* 
            {productData.inhaltstyp && productData.inhaltstyp=='Packshot' && (
              <div 
                className="bg-white rounded-lg border border-gray-200 p-3 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => {
                  if (productData.url) {
                    window.open(productData.url, '_blank');
                  } else {
                    toast.error(t('component.product_details.toast.url_empty'));
                  }
                }}
              >
                <div className="flex justify-center mb-2">
                  <BringzzIcon icon="IconImage" folder="LineIcons" size="24" className="text-gray-600" />
                </div>
                <BringzzText className="text-xs font-bold text-gray-900 mb-1">{t('component.product_details.info_cards.package_shot.title')}</BringzzText>
                <BringzzText className="text-xs font-bold text-gray-900">{t('component.product_details.info_cards.package_shot.subtitle')}</BringzzText>
              </div>
            )}

            {productData.inhaltstyp && (productData.inhaltstyp=='Produktbroschuere' || productData.inhaltstyp=='Schulungsmaterial') && (
              <div 
                className="bg-white rounded-lg border border-gray-200 p-3 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => {
                  if (productData.url) {
                    window.open(productData.url, '_blank');
                  } else {
                    toast.error(t('component.product_details.toast.url_empty'));
                  }
                }}
              >
                <div className="flex justify-center mb-2">
                  <BringzzIcon icon="IconPaperclip" folder="LineIcons" size="24" className="text-gray-600" />
                </div>
                <BringzzText className="text-xs font-bold text-gray-900 mb-1">{t('component.product_details.info_cards.attachments.title')}</BringzzText>
                <BringzzText className="text-xs font-bold text-gray-900">{t('component.product_details.info_cards.attachments.subtitle')}</BringzzText>
              </div>
            )}

            {productData.inhaltstyp && productData.inhaltstyp=='Galeriebild' && (
              <div 
                className="bg-white rounded-lg border border-gray-200 p-3 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => {
                  if (productData.url) {
                    window.open(productData.url, '_blank');
                  } else {
                    toast.error(t('component.product_details.toast.url_empty'));
                  }
                }}
              >
                <div className="flex justify-center mb-2">
                  <BringzzIcon icon="IconGallery" folder="LineIcons" size="24" className="text-gray-600" />
                </div>
                <BringzzText className="text-xs font-bold text-gray-900 mb-1">{t('component.product_details.info_cards.gallery_image.title')}</BringzzText>
                <BringzzText className="text-xs font-bold text-gray-900">{t('component.product_details.info_cards.gallery_image.subtitle')}</BringzzText>
              </div>
            )}

            {productData.inhaltstyp && productData.inhaltstyp=='Etikettierung' && (
              <div 
                className="bg-white rounded-lg border border-gray-200 p-3 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => {
                  if (productData.url) {
                    window.open(productData.url, '_blank');
                  } else {
                    toast.error(t('component.product_details.toast.url_empty'));
                  }
                }}
              >
                <div className="flex justify-center mb-2">
                  <BringzzIcon icon="IconTag" folder="LineIcons" size="24" className="text-gray-600" />
                </div>
                <BringzzText className="text-xs font-bold text-gray-900 mb-1">{t('component.product_details.info_cards.labeling.title')}</BringzzText>
                <BringzzText className="text-xs font-bold text-gray-900">{t('component.product_details.info_cards.labeling.subtitle')}</BringzzText>
              </div>
            )}

            {productData.inhaltstyp && productData.inhaltstyp=='Produktvideo' && (
              <div 
                className="bg-white rounded-lg border border-gray-200 p-3 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => {
                  if (productData.url) {
                    window.open(productData.url, '_blank');
                  } else {
                    toast.error(t('component.product_details.toast.url_empty'));
                  }
                }}
              >
                <div className="flex justify-center mb-2">
                  <BringzzIcon icon="IconVideo" folder="LineIcons" size="24" className="text-gray-600" />
                </div>
                <BringzzText className="text-xs font-bold text-gray-900 mb-1">{t('component.product_details.info_cards.product_video.title')}</BringzzText>
                <BringzzText className="text-xs font-bold text-gray-900">{t('component.product_details.info_cards.product_video.subtitle')}</BringzzText>
              </div>
            )}
            */}

              <div className="bg-white rounded-lg border border-gray-200 p-3 text-center flex-1 min-w-0">
                <div className="flex justify-center mb-2">
                  <BringzzIcon icon="IconCalendar" folder="LineIcons" size="24" className="text-gray-600" />
                </div>
                <BringzzText className="text-xs font-bold text-gray-900 mb-1">{t('component.product_details.info_cards.release')}</BringzzText>
                <BringzzText className="text-xs font-bold text-gray-900">{new Date(productData.created).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</BringzzText>
              </div>
            </div>

            <div className="mt-8 px-4">
              <BringzzCallToAction
                text={t('component.product_details.cta.pharmacist_advice')}
                // primaryButton={{
                //   label: t('component.product_details.cta.chat'),
                //   className: 'bg-emerald-500 text-white hover:bg-emerald-600 transition-colors',
                //   onClick: () => navigate('/settings/chats')
                // }}
                secondaryButton={{
                  label: t('component.product_details.cta.call'),
                  className: 'bg-blue-500 text-white hover:bg-blue-600 transition-colors',
                  onClick: () => window.location.href = `tel:${productData.pharmacyTelefon}`
                }}
                image={ManCallToAction}
              />
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200 text-center px-4">
              <BringzzImage
                src="/BringzzLogo.png"
                alt="Bringzz Logo"
                className="h-8 mx-auto mb-4"
              />
              <BringzzText className="text-gray-500 text-sm">
                © 2025
              </BringzzText>
            </div>
          </div>
        </div>
      </div>

      {showPackagingModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setShowPackagingModal(false)}
          />

          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-sm mx-4 w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <BringzzText tag="h2" className="text-lg font-semibold text-gray-900">
                  {t('component.product_details.modals.select_application_form')}
                </BringzzText>

                <button
                  onClick={() => setShowPackagingModal(false)}
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                >
                  <BringzzIcon
                    icon="IconX"
                    folder="LineIcons"
                    size="18"
                    className="text-gray-600"
                  />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-3">
              {[
                { label: 'Tabletten', sublabel: 'Kapseln', form: 'Tablets' },
                { label: 'Sirup', sublabel: 'Lösung', form: 'Syrup', selected: true },
                { label: 'Tropfen', sublabel: '', form: 'Drops' }
              ].map((option, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${option.selected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    }`}
                  onClick={() => {
                    setSelectedConfiguration({
                      ...selectedConfiguration,
                      packaging: option.form
                    });
                  }}
                >
                  <BringzzText className={`font-medium ${option.selected ? 'text-blue-900' : 'text-gray-900'}`}>
                    {option.label}
                  </BringzzText>
                  {option.sublabel && (
                    <BringzzText className={`text-sm mt-1 ${option.selected ? 'text-blue-700' : 'text-gray-600'}`}>
                      {option.sublabel}
                    </BringzzText>
                  )}
                </div>
              ))}
            </div>

            {/* Apply Button */}
            <div className="p-6 pt-0">
              <BringzzButton
                onClick={() => setShowPackagingModal(false)}
                className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
              >
                {t('component.product_details.modals.apply')}
              </BringzzButton>
            </div>
          </div>
        </div>
      )}

      {selectedModal && (
        <div className="fixed inset-0 z-50 flex items-end">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setSelectedModal(null)}
          />

          <div className="relative bg-white w-full rounded-t-3xl shadow-2xl animate-slide-up">
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
            </div>

            <div className="px-6 pb-6">
              <BringzzText tag="h2" className="text-xl font-bold text-gray-900 mb-4">
                {selectedModal === 'form' && t('component.product_details.modals.form')}
                {selectedModal === 'strength' && t('component.product_details.modals.strength')}
                {selectedModal === 'package' && t('component.product_details.modals.package')}
              </BringzzText>

              <div className="space-y-3">
                {selectedModal === 'form' && (
                  productData.formVariants?.length > 1 ? (
                    productData.formVariants.map((variant) => (
                      <div
                        key={variant.integrationId}
                        onClick={() => {
                          if (!variant.isCurrent) {
                            setSelectedModal(null);
                            navigate(`/product/details/${variant.integrationId}`);
                          }
                        }}
                        className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                          variant.isCurrent
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 bg-gray-50 hover:border-gray-300 cursor-pointer'
                        }`}
                      >
                        <BringzzText className={`font-medium ${variant.isCurrent ? 'text-purple-900' : 'text-gray-900'}`}>
                          {variant.formLabel}
                        </BringzzText>
                      </div>
                    ))
                  ) : (
                    <BringzzText className="text-gray-700 leading-relaxed">
                      {productData.formLabel || productData.pharmaForm || t('component.product_details.modals.field_not_provided')}
                    </BringzzText>
                  )
                )}

                {selectedModal === 'strength' && (
                  <div className="space-y-2">
                    {productData.strengthEntries?.length > 0 ? (
                      productData.strengthEntries.map((entry, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <BringzzText className="text-gray-700">
                            {entry.minStrength}
                            {entry.maxStrength && entry.maxStrength !== entry.minStrength
                              ? ` - ${entry.maxStrength}`
                              : ''}
                            {entry.unitLabel ? ` ${entry.unitLabel}` : ''}
                          </BringzzText>
                        </div>
                      ))
                    ) : (
                      <BringzzText className="text-gray-700 leading-relaxed">
                        {productData.preparation_information || t('component.product_details.modals.field_not_provided')}
                      </BringzzText>
                    )}
                  </div>
                )}

                {selectedModal === 'package' && (
                  productData.packageVariants?.length > 1 ? (
                    productData.packageVariants.map((variant) => (
                      <div
                        key={variant.integrationId}
                        onClick={() => {
                          if (!variant.isCurrent) {
                            setSelectedModal(null);
                            navigate(`/product/details/${variant.integrationId}`);
                          }
                        }}
                        className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                          variant.isCurrent
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 bg-gray-50 hover:border-gray-300 cursor-pointer'
                        }`}
                      >
                        {variant.productName && (
                          <span className={`block text-xs mb-1 ${variant.isCurrent ? 'text-purple-700' : 'text-gray-500'}`}>
                            {variant.productName}
                          </span>
                        )}
                        <BringzzText className={`font-medium ${variant.isCurrent ? 'text-purple-900' : 'text-gray-900'}`}>
                          {variant.packSize} {variant.packSizeUnit}
                        </BringzzText>
                      </div>
                    ))
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <BringzzText className="font-medium text-gray-900 mb-1">{t('component.product_details.modals.package_size')}:</BringzzText>
                        <BringzzText className="text-gray-700">
                          {productData.packageQuantity && productData.packaging
                            ? `${productData.packageQuantity} ${productData.packaging}`
                            : t('component.product_details.modals.field_not_provided')}
                        </BringzzText>
                      </div>
                      <div>
                        <BringzzText className="font-medium text-gray-900 mb-1">{t('component.product_details.modals.unit')}:</BringzzText>
                        <BringzzText className="text-gray-700">
                          {productData.pack_size_unit_abb || t('component.product_details.modals.field_not_provided')}
                        </BringzzText>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <BookmarkCollectionDrawer
        isOpen={showBookmarkDrawer}
        onClose={() => setShowBookmarkDrawer(false)}
        collections={collections}
        onSelectCollection={(collectionId) => addBookmarkToCollection(productData.integrationId, collectionId)}
        onCreateCollection={createNewCollection}
      />

      <BringzzBasketDrawer
        products={addedProduct ? [addedProduct] : []}
        isOpen={showAddToBasketModal}
        basketContainerProps={{ className: '' }}
        continueShoppingBtn={handleContinueShopping}
        goToBasketBtn={handleGoToBasket}
      />

    </BringzzPageContainer>
  );
}

export default ProductDetails;